/**
 * Challenge Details Content.
 */
import { FC, ReactNode, useCallback, useContext, useMemo } from 'react'
import { toast } from 'react-toastify'

import { ActionLoading } from '~/apps/admin/src/lib'

import { ChallengeDetailContext } from '../../contexts'
import {
    BackendSubmission,
    ChallengeDetailContextModel,
    MappingReviewAppeal,
    Screening,
    SubmissionInfo,
} from '../../models'
import {
    useDownloadSubmission,
    useDownloadSubmissionProps,
    useRole,
    useRoleProps,
    useSubmissionDownloadAccess,
    UseSubmissionDownloadAccessResult,
} from '../../hooks'
import {
    useFetchChallengeResults,
    useFetchChallengeResultsProps,
} from '../../hooks/useFetchChallengeResults'
import { ITERATIVE_REVIEW, SUBMITTER } from '../../../config/index.config'
import { TableNoRecord } from '../TableNoRecord'
import { hasIsLatestFlag } from '../../utils'
import {
    isContestReviewPhaseSubmission,
    shouldIncludeInReviewPhase,
} from '../../utils/reviewPhaseGuards'

import TabContentApproval from './TabContentApproval'
import TabContentCheckpoint from './TabContentCheckpoint'
import TabContentIterativeReview from './TabContentIterativeReview'
import TabContentRegistration from './TabContentRegistration'
import TabContentReview from './TabContentReview'
import TabContentScreening from './TabContentScreening'
import TabContentSubmissions from './TabContentSubmissions'
import TabContentWinners from './TabContentWinners'

const normalizeType = (value?: string): string => (
    value
        ? value
            .toLowerCase()
            .replace(/[^a-z]/g, '')
        : ''
)

interface Props {
    selectedTab: string
    isLoadingSubmission: boolean
    screening: Screening[]
    screeningMinimumPassingScore: number | null | undefined
    submissions: BackendSubmission[]
    checkpoint: Screening[]
    checkpointScreeningMinimumPassingScore: number | null | undefined
    checkpointReview: Screening[]
    checkpointReviewMinimumPassingScore: number | null | undefined
    review: SubmissionInfo[]
    reviewMinimumPassingScore: number | null | undefined
    submitterReviews: SubmissionInfo[]
    approvalReviews: SubmissionInfo[]
    approvalMinimumPassingScore: number | null | undefined
    postMortemReviews: SubmissionInfo[]
    postMortemMinimumPassingScore: number | null | undefined
    mappingReviewAppeal: MappingReviewAppeal // from review id to appeal info
    isActiveChallenge: boolean
    selectedPhaseId?: string
}

// Internal wrapper to match tab layout spacing
const TabContentWrapper = (props: { children: ReactNode }): JSX.Element => <>{props.children}</>

// Lightweight wrapper to reuse existing empty-state style
const TabContentPlaceholder = (props: { message: string }): JSX.Element => (
    <TabContentWrapper>
        <TableNoRecord message={props.message} />
    </TabContentWrapper>
)

const SUBMISSION_TAB_KEYS = new Set([
    normalizeType('submission'),
    normalizeType('specification submission'),
    normalizeType('screening'),
    normalizeType('ai screening'),
    normalizeType('submission / screening'),
    normalizeType('topgear submission'),
])

const CHECKPOINT_REVIEW_KEY = normalizeType('checkpoint review')
const CHECKPOINT_SCREENING_KEY = normalizeType('checkpoint screening')
const AI_SCREENING_KEY = normalizeType('ai screening')
const CHECKPOINT_TAB_KEYS = new Set([
    normalizeType('checkpoint'),
    normalizeType('checkpoint submission'),
    CHECKPOINT_SCREENING_KEY,
    CHECKPOINT_REVIEW_KEY,
])

interface BuildScreeningRowsParams {
    screening: Screening[]
    actionChallengeRole: useRoleProps['actionChallengeRole']
    currentMemberId: UseSubmissionDownloadAccessResult['currentMemberId']
}

const buildScreeningRows = ({
    screening,
    actionChallengeRole,
    currentMemberId,
}: BuildScreeningRowsParams): Screening[] => {
    if (actionChallengeRole === SUBMITTER && currentMemberId) {
        const mySubmissions = screening.filter(entry => entry.memberId === currentMemberId)

        return hasIsLatestFlag(mySubmissions)
            ? mySubmissions.filter(submission => submission.isLatest === true)
            : mySubmissions
    }

    return screening
}

interface SubmissionTabParams {
    selectedTabNormalized: string
    allowTopgearSubmissionList: boolean
    submissions: BackendSubmission[]
    screeningRows: Screening[]
    screeningMinimumPassingScore: number | null | undefined
    isLoadingSubmission: boolean
    isDownloadingSubmission: useDownloadSubmissionProps['isLoading']
    downloadSubmission: useDownloadSubmissionProps['downloadSubmission']
    isActiveChallenge: boolean
    aiReviewers: { aiWorkflowId: string }[]
}

const renderSubmissionTab = ({
    selectedTabNormalized,
    allowTopgearSubmissionList,
    submissions,
    screeningRows,
    screeningMinimumPassingScore,
    isLoadingSubmission,
    isDownloadingSubmission,
    downloadSubmission,
    isActiveChallenge,
    aiReviewers,
}: SubmissionTabParams): JSX.Element => {
    const isSubmissionTab = selectedTabNormalized === 'submission'
    const isSpecificationSubmissionTab = selectedTabNormalized === 'specificationsubmission'
    const isTopgearSubmissionTab = selectedTabNormalized === 'topgearsubmission'
    const shouldRestrictToContestSubmissions = selectedTabNormalized
        .startsWith('submission')
        || isSpecificationSubmissionTab
        || selectedTabNormalized === AI_SCREENING_KEY
        || isTopgearSubmissionTab
    const visibleSubmissions = shouldRestrictToContestSubmissions
        ? submissions.filter(
            submission => normalizeType(submission.type) === 'contestsubmission',
        )
        : submissions
    const canShowSubmissionList = (allowTopgearSubmissionList || !isTopgearSubmissionTab)
        && selectedTabNormalized !== 'screening'
        && visibleSubmissions.length > 0

    if (canShowSubmissionList) {
        return (
            <TabContentSubmissions
                aiReviewers={aiReviewers}
                submissions={visibleSubmissions}
                isLoading={isLoadingSubmission}
                isDownloading={isDownloadingSubmission}
                downloadSubmission={downloadSubmission}
            />
        )
    }

    return (
        <TabContentScreening
            screening={screeningRows}
            screeningMinimumPassingScore={screeningMinimumPassingScore}
            isLoadingScreening={isLoadingSubmission}
            isDownloading={isDownloadingSubmission}
            downloadSubmission={downloadSubmission}
            isActiveChallenge={isActiveChallenge}
            showScreeningColumns={!isSubmissionTab && !isTopgearSubmissionTab}
            aiReviewers={aiReviewers}
        />
    )
}

export const ChallengeDetailsContent: FC<Props> = (props: Props) => {
    const {
        challengeInfo,
        myResources,
    }: ChallengeDetailContextModel = useContext(ChallengeDetailContext)
    const { actionChallengeRole }: useRoleProps = useRole()
    const hasIterativeReviewerRole = useMemo(
        () => myResources.some(
            resource => resource.roleName
                ?.toLowerCase()
                .includes('iterative reviewer'),
        ),
        [myResources],
    )
    const allowTopgearSubmissionList = useMemo(
        () => actionChallengeRole !== SUBMITTER || hasIterativeReviewerRole,
        [actionChallengeRole, hasIterativeReviewerRole],
    )
    const { currentMemberId }: UseSubmissionDownloadAccessResult = useSubmissionDownloadAccess()
    const {
        isLoading: isDownloadingSubmission,
        isLoadingBool: isDownloadingSubmissionBool,
        downloadSubmission,
    }: useDownloadSubmissionProps = useDownloadSubmission()
    const submissionsById = useMemo(() => {
        const map = new Map<string, BackendSubmission>()
        props.submissions.forEach(submission => {
            if (submission?.id) {
                map.set(submission.id, submission)
            }
        })
        return map
    }, [props.submissions])
    const handleSubmissionDownload = useCallback((submissionId: string) => {
        const submission = submissionsById.get(submissionId)
        if (submission && submission.isFileSubmission === false) {
            const targetUrl = submission.url?.trim()
            if (targetUrl) {
                if (typeof window !== 'undefined') {
                    window.open(targetUrl, '_blank', 'noopener,noreferrer')
                } else {
                    toast.error('Unable to open the submission URL from this environment.')
                }
            } else {
                toast.error('Submission URL is not available for this entry.')
            }

            return
        }

        downloadSubmission(submissionId)
    }, [downloadSubmission, submissionsById])
    const {
        isLoading: isLoadingProjectResult,
        projectResults,
    }: useFetchChallengeResultsProps = useFetchChallengeResults(props.review)
    const selectedTabNormalized = useMemo(
        () => normalizeType(props.selectedTab),
        [props.selectedTab],
    )
    const selectedReviewPhaseName = useMemo<string | undefined>(
        () => {
            if (selectedTabNormalized === 'specificationreview') {
                return 'Specification Review'
            }

            if (selectedTabNormalized === 'review') {
                return 'Review'
            }

            return undefined
        },
        [selectedTabNormalized],
    )

    // Determine if the selected tab corresponds to a phase that hasn't opened yet
    const selectedPhase = useMemo(
        () => (props.selectedPhaseId
            ? (challengeInfo?.phases || []).find(p => p.id === props.selectedPhaseId)
            : undefined),
        [challengeInfo?.phases, props.selectedPhaseId],
    )
    const isFuturePhaseForSubmitter = useMemo(() => {
        if (!props.isActiveChallenge) return false
        if (actionChallengeRole !== SUBMITTER) return false
        if (!selectedPhase) return false
        const isOpen = Boolean((selectedPhase as { isOpen?: boolean }).isOpen)
        const hasStarted = Boolean(selectedPhase.actualStartDate)
        // If phase is not open and hasn't actually started, consider it future
        if (!isOpen && !hasStarted) return true
        // Fallback to scheduled start in the future if available
        const startMs = Date.parse(selectedPhase.actualStartDate || selectedPhase.scheduledStartDate || '')
        if (Number.isFinite(startMs)) {
            return startMs > Date.now()
        }

        return false
    }, [actionChallengeRole, selectedPhase, props.isActiveChallenge])
    const unopenedPhaseMessage = useMemo(() => {
        if (!selectedPhase) return undefined
        const name = (selectedPhase.name || props.selectedTab || 'selected').toLowerCase()
        return `The ${name} phase hasn't opened yet.`
    }, [selectedPhase, props.selectedTab])
    const postMortemReviewRows = useMemo(
        () => props.postMortemReviews,
        [props.postMortemReviews],
    )
    const postMortemSubmitterReviews = useMemo(() => {
        const filtered = props.submitterReviews.filter(
            submission => normalizeType(submission.reviewTypeId)
                .includes('postmortem'),
        )

        return filtered.length ? filtered : props.postMortemReviews
    }, [props.postMortemReviews, props.submitterReviews])
    const hasScreeningPhase = useMemo(
        () => (challengeInfo?.phases ?? []).some(
            phase => (phase.name || '').trim()
                .toLowerCase() === 'screening',
        ),
        [challengeInfo?.phases],
    )
    const screeningOutcome = useMemo(
        () => {
            const passingSubmissionIds = new Set<string>()
            const failingSubmissionIds = new Set<string>()

            props.screening.forEach(entry => {
                if (!entry?.submissionId) {
                    return
                }

                const normalizedResult = (entry.result || '').toUpperCase()
                if (normalizedResult === 'PASS') {
                    passingSubmissionIds.add(`${entry.submissionId}`)
                } else if (normalizedResult === 'NO PASS') {
                    failingSubmissionIds.add(`${entry.submissionId}`)
                }
            })

            return {
                failingSubmissionIds,
                passingSubmissionIds,
            }
        },
        [props.screening],
    )
    const passesReviewTabGuards: (submission: SubmissionInfo) => boolean = useMemo(
        () => (submission: SubmissionInfo): boolean => {
            if (selectedReviewPhaseName) {
                return isContestReviewPhaseSubmission(
                    submission,
                    challengeInfo?.phases,
                    selectedReviewPhaseName,
                )
            }

            return shouldIncludeInReviewPhase(
                submission,
                challengeInfo?.phases,
            )
        },
        [challengeInfo?.phases, selectedReviewPhaseName],
    )
    const {
        reviews: reviewTabReviews,
        submitterReviews: reviewTabSubmitterReviews,
    }: {
        reviews: SubmissionInfo[]
        submitterReviews: SubmissionInfo[]
    } = useMemo(() => {
        const {
            failingSubmissionIds,
            passingSubmissionIds,
        }: {
            failingSubmissionIds: Set<string>
            passingSubmissionIds: Set<string>
        } = screeningOutcome
        const shouldFilter = props.isActiveChallenge
            && (hasScreeningPhase || props.screening.length > 0)
            && (passingSubmissionIds.size > 0 || failingSubmissionIds.size > 0)

        const matchesScreeningOutcome = (submission: SubmissionInfo): boolean => {
            if (!shouldFilter) {
                return true
            }

            if (!submission) {
                return false
            }

            const candidateIds: string[] = []
            if (submission.id) {
                candidateIds.push(`${submission.id}`)
            }

            const reviewSubmissionId = submission.review?.submissionId
            if (reviewSubmissionId) {
                candidateIds.push(`${reviewSubmissionId}`)
            }

            if (!candidateIds.length) {
                return true
            }

            if (passingSubmissionIds.size > 0) {
                return candidateIds.some(id => passingSubmissionIds.has(id))
            }

            if (failingSubmissionIds.size > 0) {
                return !candidateIds.some(id => failingSubmissionIds.has(id))
            }

            return candidateIds.some(id => passingSubmissionIds.has(id))
        }

        return {
            reviews: props.review
                .filter(matchesScreeningOutcome)
                .filter(passesReviewTabGuards),
            submitterReviews: props.submitterReviews
                .filter(matchesScreeningOutcome)
                .filter(passesReviewTabGuards),
        }
    }, [
        hasScreeningPhase,
        props.isActiveChallenge,
        props.review,
        props.submitterReviews,
        props.screening,
        props.screening.length,
        passesReviewTabGuards,
        screeningOutcome,
    ])

    const renderSelectedTab = (): JSX.Element => {
        const selectedTabLower = (props.selectedTab || '').toLowerCase()
        const aiReviewers = (
            challengeInfo?.reviewers?.filter(r => !!r.aiWorkflowId) as { aiWorkflowId: string }[]
        ) ?? []

        if (selectedTabLower === 'registration') {
            return <TabContentRegistration />
        }

        const screeningRows = buildScreeningRows({
            actionChallengeRole,
            currentMemberId,
            screening: props.screening,
        })

        if (SUBMISSION_TAB_KEYS.has(selectedTabNormalized)) {
            return renderSubmissionTab({
                aiReviewers,
                allowTopgearSubmissionList,
                downloadSubmission: handleSubmissionDownload,
                isActiveChallenge: props.isActiveChallenge,
                isDownloadingSubmission,
                isLoadingSubmission: props.isLoadingSubmission,
                screeningMinimumPassingScore: props.screeningMinimumPassingScore,
                screeningRows,
                selectedTabNormalized,
                submissions: props.submissions,
            })
        }

        if (CHECKPOINT_TAB_KEYS.has(selectedTabNormalized)) {
            const checkpointMode: 'submission' | 'screening' | 'review' = selectedTabNormalized
                .startsWith(CHECKPOINT_REVIEW_KEY)
                ? 'review'
                : selectedTabNormalized.startsWith(CHECKPOINT_SCREENING_KEY)
                    ? 'screening'
                    : 'submission'
            return (
                <TabContentCheckpoint
                    checkpoint={props.checkpoint}
                    checkpointReview={props.checkpointReview}
                    checkpointScreeningMinimumPassingScore={props.checkpointScreeningMinimumPassingScore}
                    checkpointReviewMinimumPassingScore={props.checkpointReviewMinimumPassingScore}
                    isLoading={props.isLoadingSubmission}
                    isDownloading={isDownloadingSubmission}
                    downloadSubmission={handleSubmissionDownload}
                    mode={checkpointMode}
                    aiReviewers={aiReviewers}
                />
            )
        }

        if (selectedTabLower === 'winners') {
            return (
                <TabContentWinners
                    aiReviewers={aiReviewers}
                    isLoading={isLoadingProjectResult}
                    projectResults={projectResults}
                    isDownloading={isDownloadingSubmission}
                    downloadSubmission={handleSubmissionDownload}
                />
            )
        }

        if (selectedTabNormalized === 'approval') {
            return (
                <TabContentApproval
                    reviews={props.approvalReviews}
                    submitterReviews={props.submitterReviews}
                    approvalMinimumPassingScore={props.approvalMinimumPassingScore}
                    phaseIdFilter={props.selectedPhaseId}
                    isLoadingReview={props.isLoadingSubmission}
                    isDownloading={isDownloadingSubmission}
                    downloadSubmission={handleSubmissionDownload}
                    isActiveChallenge={props.isActiveChallenge}
                />
            )
        }

        if (selectedTabLower === 'post-mortem') {
            return (
                <TabContentIterativeReview
                    reviews={postMortemReviewRows}
                    submitterReviews={postMortemSubmitterReviews}
                    postMortemMinimumPassingScore={props.postMortemMinimumPassingScore}
                    isLoadingReview={props.isLoadingSubmission}
                    isDownloading={isDownloadingSubmission}
                    downloadSubmission={handleSubmissionDownload}
                    isActiveChallenge={props.isActiveChallenge}
                    columnLabel='Post-Mortem'
                    aiReviewers={aiReviewers}
                />
            )
        }

        if (props.selectedTab.startsWith(ITERATIVE_REVIEW)) {
            return (
                <TabContentIterativeReview
                    reviews={props.review}
                    submitterReviews={props.submitterReviews}
                    postMortemMinimumPassingScore={props.postMortemMinimumPassingScore}
                    isLoadingReview={props.isLoadingSubmission}
                    isDownloading={isDownloadingSubmission}
                    downloadSubmission={handleSubmissionDownload}
                    isActiveChallenge={props.isActiveChallenge}
                    phaseIdFilter={props.selectedPhaseId}
                    aiReviewers={aiReviewers}
                />
            )
        }

        return (
            <TabContentReview
                aiReviewers={aiReviewers}
                selectedTab={props.selectedTab}
                reviews={reviewTabReviews}
                submitterReviews={reviewTabSubmitterReviews}
                screeningOutcome={screeningOutcome}
                reviewMinimumPassingScore={props.reviewMinimumPassingScore}
                isLoadingReview={props.isLoadingSubmission}
                isDownloading={isDownloadingSubmission}
                downloadSubmission={handleSubmissionDownload}
                mappingReviewAppeal={props.mappingReviewAppeal}
                isActiveChallenge={props.isActiveChallenge}
            />
        )
    }

    return (
        <>
            {isFuturePhaseForSubmitter ? (
                <TabContentPlaceholder message={unopenedPhaseMessage || "This phase hasn't opened yet."} />
            ) : (
                renderSelectedTab()
            )}

            {isDownloadingSubmissionBool && <ActionLoading />}
        </>
    )
}

export default ChallengeDetailsContent
